import { Link, useSearchParams } from "react-router-dom";
import type { AlertState, Severity } from "../api/types";
import { ALERT_STATES, SEVERITIES } from "../api/types";
import { useAckAlert, useAlerts } from "../hooks/queries";
import { useAuth } from "../auth/AuthContext";
import { can } from "../auth/rbac";
import {
  AlertStateBadge,
  SeverityBadge,
} from "../components/domain/Badges";
import { Button } from "../components/ui/Button";
import { Card, CardContent } from "../components/ui/Card";
import { Select } from "../components/ui/Input";
import { Pagination } from "../components/ui/Pagination";
import { TableSkeleton } from "../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../components/ui/States";
import { Table, TBody, TD, TH, THead, TR } from "../components/ui/Table";
import { useToast } from "../components/ui/Toast";
import { errorMessage } from "../api/errors";
import { formatDateTime, formatNumber, formatRelative } from "../lib/format";

const stateOptions = [
  { value: "", label: "Tümü" },
  ...ALERT_STATES.map((state) => ({
    value: state,
    label:
      state === "firing"
        ? "Tetiklendi"
        : state === "resolved"
          ? "Çözüldü"
          : state === "acknowledged"
            ? "Onaylandı"
            : "Sessize alındı",
  })),
];

const severityOptions = [
  { value: "", label: "Tümü" },
  ...SEVERITIES.map((severity) => ({
    value: severity,
    label:
      severity === "critical"
        ? "Kritik"
        : severity === "high"
          ? "Yüksek"
          : severity === "medium"
            ? "Orta"
            : severity === "low"
              ? "Düşük"
              : "Bilinmiyor",
  })),
];

export function AlertsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const ackMutation = useAckAlert();

  const query = {
    state: (searchParams.get("state") as AlertState | null) ?? undefined,
    severity: (searchParams.get("severity") as Severity | null) ?? undefined,
    deviceId: searchParams.get("deviceId") ?? undefined,
    page: Number(searchParams.get("page") ?? "1") || 1,
    perPage: Number(searchParams.get("perPage") ?? "25") || 25,
  };
  const alertsQuery = useAlerts(query);

  function updateParams(patch: Record<string, string | undefined>) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined || value === "") next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  const canAck = can(user?.role, "ackAlerts");

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Uyarılar</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Aktif ve geçmiş uyarılar; operatör/yönetici rolleri onaylayabilir.
        </p>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Select
              label="Durum"
              options={stateOptions}
              value={query.state ?? ""}
              onChange={(event) =>
                updateParams({ state: event.target.value || undefined, page: undefined })
              }
            />
            <Select
              label="Önem"
              options={severityOptions}
              value={query.severity ?? ""}
              onChange={(event) =>
                updateParams({ severity: event.target.value || undefined, page: undefined })
              }
            />
            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
              >
                Filtreleri temizle
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {alertsQuery.isPending ? (
            <TableSkeleton rows={7} columns={6} />
          ) : alertsQuery.isError ? (
            <ErrorState
              message="Uyarı listesi yüklenemedi."
              onRetry={() => void alertsQuery.refetch()}
            />
          ) : (alertsQuery.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              title="Uyarı bulunamadı"
              description="Seçilen filtrelere uyan uyarı yok."
              actionLabel="Filtreleri temizle"
              onAction={() => setSearchParams(new URLSearchParams(), { replace: true })}
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Cihaz</TH>
                  <TH>Mesaj</TH>
                  <TH>Değer</TH>
                  <TH>Önem</TH>
                  <TH>Durum</TH>
                  <TH>Başlangıç</TH>
                  <TH>İşlem</TH>
                </TR>
              </THead>
              <TBody>
                {alertsQuery.data?.items.map((alert) => (
                  <TR key={alert.id}>
                    <TD>
                      <Link
                        to={`/devices/${alert.deviceId}`}
                        className="text-primary font-medium hover:underline"
                      >
                        {alert.hostname}
                      </Link>
                    </TD>
                    <TD className="max-w-md">
                      <p className="truncate" title={alert.message}>
                        {alert.message}
                      </p>
                      {alert.acknowledgedBy ? (
                        <p className="text-muted-foreground text-xs">
                          Onaylayan: {alert.acknowledgedBy}
                        </p>
                      ) : null}
                    </TD>
                    <TD className="text-sm">{formatNumber(alert.value, 1)}</TD>
                    <TD>
                      <SeverityBadge severity={alert.severity} />
                    </TD>
                    <TD>
                      <AlertStateBadge state={alert.state} />
                    </TD>
                    <TD className="text-muted-foreground whitespace-nowrap">
                      <span title={formatDateTime(alert.startedAt)}>
                        {formatRelative(alert.startedAt)}
                      </span>
                    </TD>
                    <TD>
                      {alert.state === "firing" || alert.state === "suppressed" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!canAck || ackMutation.isPending}
                          onClick={() => {
                            ackMutation.mutate(alert.id, {
                              onSuccess: () => toast("Uyarı onaylandı.", "success"),
                              onError: (error) =>
                                toast(errorMessage(error), "error"),
                            });
                          }}
                        >
                          Onayla
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}

          {alertsQuery.data ? (
            <Pagination
              page={alertsQuery.data.pageMeta.page}
              perPage={alertsQuery.data.pageMeta.perPage}
              total={alertsQuery.data.pageMeta.total}
              onPageChange={(next) => updateParams({ page: String(next) })}
              onPerPageChange={(next) =>
                updateParams({ perPage: String(next), page: undefined })
              }
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}